import { createSlice } from '@reduxjs/toolkit';

/**
 * Room cards setup — the Card Stock tab (docs/plans/room-cards-setup/PLAN.md v0.7.9 §5.2, R8–R10, work row 10).
 * Dark behind `credentials.roomStays`.
 *
 * Per site: the draft list being enrolled (cards already normalised by the tab, their classify results and per-card
 * confirmations, and the list's `enrolKey`), the enrolment operation's progress, and the site's Card Stock and Card
 * Store cards. Nothing here decides access or delivery: every state shown is the server's (INV-10).
 *
 * `enrolKey` belongs to the draft, together with the exact cards it was sent for (`enrolKeyFor`). Resending the same
 * cards (a timeout, a 5xx) reuses the key, so the server replays the first enrolment instead of starting a second; a
 * changed list gets a new key, so it is never refused as a key conflict for an edit staff made on purpose.
 */

const emptyDraft = () => ({
	rows: [],
	nextID: 1,
	lastAdd: null,
	classifying: false,
	classifyError: null,
	enrolKey: null,
	enrolKeyFor: null,
	/** Which submission is in flight: an enrol answer is applied only to the draft that sent it. */
	submissionID: null,
	enrolling: false,
	enrolError: null,
	operation: null,
	progressError: null,
	/** True when polling gave up at PROGRESS_POLL_MAX with the operation still working: staff refresh by hand. */
	progressStopped: false
});

const initialState = {
	drafts: {},
	cardsBySite: {},
	cardsLoading: {},
	cardsError: {},
	/** `${siteID}:${credentialID}` → true while that card's repair runs. */
	repairing: {},
	/** `${siteID}:${credentialID}` → { ok, result?, error? } for the last repair of that card. */
	repairResults: {}
};

/**
 * While a list is out for enrolment, or once one was accepted, it cannot change: the server may already have enrolled
 * it, and its answer, its operation and its replay key must stay with exactly the cards that were sent.
 */
const frozen = (draft) => Boolean(draft.operation || draft.enrolling);

/**
 * The draft an enrol answer belongs to, or null for a stale one (the draft was reset: a logout or org switch). A
 * stale answer is dropped rather than attached to whatever list is open now.
 */
const submittingDraft = (state, siteID, submissionID) => {
	const draft = state.drafts[siteID];
	return draft && draft.submissionID === submissionID ? draft : null;
};

const draftOf = (state, siteID) => {
	if (!state.drafts[siteID]) state.drafts[siteID] = emptyDraft();
	return state.drafts[siteID];
};

const sameHolder = (a, b) =>
	a &&
	b &&
	a.status === b.status &&
	Number(a.credentialID) === Number(b.credentialID) &&
	(a.holderPersonID ?? null) === (b.holderPersonID ?? null) &&
	a.canConfirm === b.canConfirm;

/** A classify row staff may confirm: needs confirmation, and the server says this caller may move it (`canConfirm`). */
export const isConfirmable = (c) => c?.status === 'needs_confirmation' && c.canConfirm !== false;

const repairKey = (siteID, credentialID) => `${siteID}:${credentialID}`;

const slice = createSlice({
	name: 'cardStock',
	initialState,
	reducers: {
		/**
		 * Add cards to the draft. Each item is the tab's normalisation of one reader line, paste entry or CSV row:
		 * `{ csn, name }`, or `{ refusal }` for one that is not a card number (kept without its text, INV-07). A CSN
		 * already on the list is not added twice.
		 */
		addCards: (state, { payload: { siteID, items, source } }) => {
			const draft = draftOf(state, siteID);
			if (frozen(draft)) return;
			const seen = new Set(draft.rows.filter((r) => r.csn).map((r) => r.csn));
			const lastAdd = { added: 0, duplicates: 0, refused: 0 };
			(items || []).forEach((item, i) => {
				if (item.refusal) {
					draft.rows.push({ id: draft.nextID++, csn: null, refusal: item.refusal, source, entry: i + 1 });
					lastAdd.refused += 1;
					return;
				}
				if (seen.has(item.csn)) {
					lastAdd.duplicates += 1;
					return;
				}
				seen.add(item.csn);
				draft.rows.push({
					id: draft.nextID++,
					csn: item.csn,
					name: item.name || null,
					source,
					classification: null,
					confirmed: false
				});
				lastAdd.added += 1;
			});
			draft.lastAdd = lastAdd;
		},
		removeCard: (state, { payload: { siteID, id } }) => {
			const draft = draftOf(state, siteID);
			if (frozen(draft)) return;
			draft.rows = draft.rows.filter((r) => r.id !== id);
		},
		/** Per-card confirmation of a `needs_confirmation` card (§5.1.5). `ids` lets "confirm all inventory cards" store one per card. */
		setConfirmed: (state, { payload: { siteID, ids, confirmed } }) => {
			const draft = draftOf(state, siteID);
			if (frozen(draft)) return;
			const only = new Set(ids);
			draft.rows.forEach((r) => {
				if (only.has(r.id) && isConfirmable(r.classification)) r.confirmed = confirmed;
			});
		},
		/** Not while a submission is in flight: its answer is staff's only record of what the server enrolled. */
		clearDraft: (state, { payload: { siteID } }) => {
			if (state.drafts[siteID]?.enrolling) return;
			state.drafts[siteID] = emptyDraft();
		},

		classifyCards: (state, { payload: { siteID } }) => {
			const draft = draftOf(state, siteID);
			draft.classifying = true;
			draft.classifyError = null;
		},
		/** Merged by CSN. A card whose holder changed since the last check loses its confirmation: staff confirm what they see. */
		classifyCardsSuccess: (state, { payload: { siteID, cards } }) => {
			const draft = draftOf(state, siteID);
			draft.classifying = false;
			const byCsn = new Map((cards || []).map((c) => [String(c.csn), c]));
			draft.rows.forEach((r) => {
				const next = r.csn && byCsn.get(r.csn);
				if (!next) return;
				if (!sameHolder(r.classification, next)) r.confirmed = false;
				r.classification = next;
			});
		},
		classifyCardsError: (state, { payload: { siteID, error } }) => {
			const draft = draftOf(state, siteID);
			draft.classifying = false;
			draft.classifyError = error;
		},

		enrolCards: (state, { payload: { siteID } }) => {
			const draft = draftOf(state, siteID);
			draft.enrolling = true;
			draft.enrolError = null;
		},
		/** Recorded BEFORE the request goes out, so a resend after a timeout finds the same key. */
		setEnrolKey: (state, { payload: { siteID, enrolKey, enrolKeyFor, submissionID } }) => {
			const draft = draftOf(state, siteID);
			draft.enrolKey = enrolKey;
			draft.enrolKeyFor = enrolKeyFor;
			draft.submissionID = submissionID;
		},
		enrolCardsSuccess: (state, { payload: { siteID, submissionID, operation } }) => {
			const draft = submittingDraft(state, siteID, submissionID);
			if (!draft) return;
			draft.enrolling = false;
			draft.operation = operation;
		},
		enrolCardsError: (state, { payload: { siteID, submissionID, error } }) => {
			const draft = submittingDraft(state, siteID, submissionID);
			if (!draft) return;
			draft.enrolling = false;
			draft.enrolError = error;
		},

		/** Progress polling for the draft's operation; `stopProgress` ends it (the tab unmounting, a new list). */
		startProgress: (state, { payload: { siteID } }) => {
			draftOf(state, siteID).progressStopped = false;
		},
		stopProgress: () => {},
		progressPollExhausted: (state, { payload: { siteID } }) => {
			draftOf(state, siteID).progressStopped = true;
		},
		loadProgressSuccess: (state, { payload: { siteID, operation } }) => {
			const draft = draftOf(state, siteID);
			draft.operation = operation;
			draft.progressError = null;
		},
		loadProgressError: (state, { payload: { siteID, error } }) => {
			draftOf(state, siteID).progressError = error;
		},

		loadStockCards: (state, { payload: { siteID } }) => {
			state.cardsLoading[siteID] = true;
			state.cardsError[siteID] = null;
		},
		loadStockCardsSuccess: (state, { payload: { siteID, data } }) => {
			state.cardsLoading[siteID] = false;
			state.cardsBySite[siteID] = data;
		},
		loadStockCardsError: (state, { payload: { siteID, error } }) => {
			state.cardsLoading[siteID] = false;
			state.cardsError[siteID] = error;
		},

		repairCard: (state, { payload: { siteID, credentialID } }) => {
			state.repairing[repairKey(siteID, credentialID)] = true;
		},
		repairCardSuccess: (state, { payload: { siteID, credentialID, result } }) => {
			delete state.repairing[repairKey(siteID, credentialID)];
			state.repairResults[repairKey(siteID, credentialID)] = { ok: true, result };
		},
		repairCardError: (state, { payload: { siteID, credentialID, error } }) => {
			delete state.repairing[repairKey(siteID, credentialID)];
			state.repairResults[repairKey(siteID, credentialID)] = { ok: false, error };
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
		// An org switch must not carry org A's draft, its enrolKey or its cards into org B.
		builder.addCase('auth/switchProfileSuccess', () => initialState);
	}
});

export const cardStockActions = slice.actions;
export { repairKey };
export default slice.reducer;
